import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Check, Loader2, Send, Sparkles, SquarePen, X } from "lucide-react";
import api, { errMsg, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const SESSION_KEY = "fm_assistant_session";

/** The assistant dock. Answers come back token by token; anything that changes a trip arrives as
 *  an action card that a human has to confirm. */
export const Assistant = () => {
  const { hasFlag } = useAuth();
  const [seat, setSeat] = useState(null);  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [actions, setActions] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);
  const session = useRef(localStorage.getItem(SESSION_KEY) || "");

  useEffect(() => {
    if (!hasFlag("assistant")) return;
    api.get("/assistant/context").then(({ data }) => {
      setSeat(data);
      if (!session.current) {
        session.current = data.session_id;
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
    }).catch(() => setSeat(null));
  }, [hasFlag]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, actions]);

  const send = useCallback(async (value) => {
    const body = (value ?? text).trim();
    if (!body || busy) return;
    setText(""); setError(""); setBusy(true);
    setMessages((m) => [...m, { role: "user", text: body }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ session_id: session.current, message: body }),
      });
      if (!res.ok || !res.body) throw new Error("The assistant is not available right now.");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value: chunk, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(chunk, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          const event = JSON.parse(line);
          if (event.type === "token") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + event.text };
              return copy;
            });
          } else if (event.type === "action") {
            setActions((a) => [...a, event.action]);
          } else if (event.type === "error") {
            setError(event.text);
          }
        }
      }
    } catch (e) {
      setError(e.message || errMsg(e));
    } finally {
      setBusy(false);
    }
  }, [busy, text]);

  const resolve = async (action, confirmed) => {
    setError("");
    try {
      const path = confirmed ? "confirm" : "dismiss";
      const { data } = await api.post(`/assistant/actions/${action.id}/${path}`);
      setActions((a) => a.filter((x) => x.id !== action.id));
      if (confirmed) {
        setMessages((m) => [...m, { role: "assistant", text: data.outcome }]);
        // The page behind the dock is now stale — whoever is listening reloads its own data.
        window.dispatchEvent(new Event("fm:data-changed"));
      }
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const restart = () => {
    const fresh = crypto.randomUUID();
    session.current = fresh;
    localStorage.setItem(SESSION_KEY, fresh);
    setMessages([]);
    setActions([]);
    setError("");
    setText("");
  };

  if (!seat) return null;

  return (
    <>
      {!open && (
        <button data-testid="assistant-launcher" onClick={() => setOpen(true)}
          aria-label="Assistant"
          className="fixed bottom-[4.75rem] right-4 z-50 flex h-12 w-12 items-center justify-center gap-2 rounded-full bg-gold text-ink-900 shadow-lg shadow-black/40 transition-transform duration-200 hover:scale-105 sm:bottom-20 sm:right-5 sm:w-auto sm:px-5">
          <Sparkles className="h-4 w-4" />
          <span className="hidden text-sm font-semibold sm:inline">Assistant</span>
        </button>
      )}

      {open && (
        <div data-testid="assistant-panel"
          className="fixed bottom-0 right-0 z-50 flex h-[85vh] w-full flex-col border-l border-t border-ink-600 bg-ink-800 shadow-2xl sm:bottom-5 sm:right-5 sm:h-[560px] sm:w-[400px] sm:rounded-2xl sm:border">
          <div className="flex items-center justify-between border-b border-ink-600 px-4 py-3">
            <p className="flex items-center gap-2 font-display text-base text-slate-100">
              <Bot className="h-4 w-4 text-gold" /> Assistant
              {seat.model && (
                <span data-testid="assistant-model"
                  className="font-mono text-[9px] uppercase tracking-widest text-slate-500">
                  {seat.model}
                </span>
              )}
            </p>
            <div className="flex items-center gap-1">
              <button data-testid="assistant-new" onClick={restart} title="New conversation"
                aria-label="New conversation" className="rounded p-1.5 text-slate-500 hover:text-gold">
                <SquarePen className="h-4 w-4" />
              </button>
              <button data-testid="assistant-close" onClick={() => setOpen(false)}
                className="rounded p-1.5 text-slate-500 hover:text-slate-200">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-slate-400">
                  Ask about flights, today's runs or a pickup time. I check before I answer.
                </p>
                {(seat.suggestions || []).map((s, i) => (
                  <button key={s} data-testid={`assistant-suggestion-${i}`} onClick={() => send(s)}
                    className="block w-full rounded-lg border border-ink-600 px-3 py-2 text-left text-sm text-slate-300 transition-colors duration-200 hover:border-gold/50 hover:text-gold">
                    {s}
                  </button>
                ))}
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} data-testid={`assistant-message-${i}`}
                className={m.role === "user" ? "text-right" : ""}>
                <span className={`inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${
                  m.role === "user" ? "bg-gold/15 text-slate-100" : "bg-ink-900 text-slate-300"}`}>
                  {m.text || (busy && i === messages.length - 1
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gold" /> : "")}
                </span>
              </div>
            ))}

            {actions.map((action) => (
              <div key={action.id} data-testid={`assistant-action-${action.id}`}
                className="rounded-xl border border-gold/40 bg-gold/5 p-3">
                <p className="text-sm text-slate-100">{action.summary}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  Trip {action.trip_number} · {action.kind}
                  {action.request_only ? " · request to the office" : ""}
                </p>
                <div className="mt-3 flex gap-2">
                  <Button data-testid={`assistant-confirm-${action.id}`} size="sm"
                    className="bg-gold text-ink-900 hover:bg-gold-light"
                    onClick={() => resolve(action, true)}>
                    <Check className="mr-1.5 h-3.5 w-3.5" /> Confirm
                  </Button>
                  <Button data-testid={`assistant-dismiss-${action.id}`} size="sm" variant="ghost"
                    onClick={() => resolve(action, false)}>Dismiss</Button>
                </div>
              </div>
            ))}

            {error && <p data-testid="assistant-error" className="text-sm text-red-400">{error}</p>}
            <div ref={endRef} />
          </div>

          <form className="flex items-center gap-2 border-t border-ink-600 p-3"
            onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input data-testid="assistant-input" value={text} disabled={busy}
              onChange={(e) => setText(e.target.value)} placeholder="Ask about a flight or a trip"
              className="h-10 flex-1 rounded-lg border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200 outline-none placeholder:text-slate-600 focus:border-gold/60" />
            <Button data-testid="assistant-send" type="submit" size="sm" disabled={busy}
              className="bg-gold text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      )}
    </>
  );
};
